import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LoggerService } from '../services/logger.service';
import { PermissionService } from '../services/permission.service';

/**
 * Factory function to create a permission guard for routes.
 *
 * @param requiredPermissions - Single permission or array of permissions required
 * @param mode - 'any' (default) or 'all' for multiple permissions
 * @returns CanActivateFn that checks permissions before route activation
 *
 * @example
 * // Single permission
 * {
 *   path: 'checkpoints/create',
 *   component: AssetCreateComponent,
 *   canActivate: [permissionGuard(['asset.create'])]
 * }
 *
 * @example
 * // Multiple permissions (OR)
 * {
 *   path: 'checkpoints/edit/:id',
 *   component: AssetEditComponent,
 *   canActivate: [permissionGuard(['asset.create', 'asset.update'])]
 * }
 *
 * @example
 * // Multiple permissions (AND)
 * {
 *   path: 'settings/billing',
 *   component: BillingComponent,
 *   canActivate: [permissionGuard(['org.settings', 'org.billing'], 'all')]
 * }
 */
export function permissionGuard(
  requiredPermissions: string | string[],
  mode: 'any' | 'all' = 'any',
): CanActivateFn {
  return (route) => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);
    const logger = inject(LoggerService).getLogger('permission-guard');

    // Normalize to array
    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];

    const routePath = route.routeConfig?.path || 'unknown';
    logger.debug(`Checking permissions for route: ${routePath}`, {
      requiredPermissions: permissions,
      mode,
    });

    // Check permissions based on mode
    let hasPermission: boolean;
    if (mode === 'all') {
      hasPermission = permissionService.hasAll(permissions);
    } else {
      hasPermission = permissionService.hasAny(permissions);
    }

    logger.debug(`Permission check result for ${routePath}:`, {
      hasPermission,
      isSuperAdmin: permissionService.isSuperAdmin(),
      userPermissions: Array.from(permissionService.permissions().entries()),
    });

    if (hasPermission) {
      logger.debug(`Permission granted for route: ${routePath}`);
      return true; // Allow navigation
    } else {
      logger.warn(`Permission DENIED for route: ${routePath}`, {
        requiredPermissions: permissions,
        mode,
        userPermissions: Array.from(permissionService.permissions().entries()),
      });
      // Redirect to forbidden page
      router.navigate(['/forbidden']);
      return false; // Block navigation
    }
  };
}
